import React, { useContext, useEffect, useMemo, useRef } from 'react';

import { TransactionGMetrixTableDialog, TransactionGMetrixTableDialogHandle } from 'components/TransactionTableGMetrixDialog/TransactionGMetrixTableDialog';
import { PartnerGMetrixTableDialog, PartnerGMetrixTableDialogHandle } from 'components/PartnerGMetrixTableDialog/PartnerGMetrixTableDialog';
import { partnerFilterPickerWording, transactionFilterPickerWording } from 'pages/reports/utils/dialogWording';
import { ReportDropdownList } from 'pages/reports/components/ReportDropdownList/ReportDropdownList';
import { GMetrixButtonGroup } from 'components/GMetrixButtonGroup/GMetrixButtonGroup';
import { getStartEndDateDefaultDays } from 'pages/reports/utils/reportDateDefaults';
import { ReportsDefaultContext } from 'pages/reports/context/ReportsDefaultContext';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { StudentGroupApi, StudentGroupWithDetails } from 'apis/StudentGroupApi';
import { Option, Report, ReportFilter, ReportFilterType } from 'apis/ReportApi';
import { StudentsContext } from 'pages/students/context/StudentsDefaultContext';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import StartDateFilter from 'components/StartDateFilter/StartDateFilter';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Transaction, TransactionApi } from 'apis/TransactionApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { Partner, PartnerApi } from 'apis/PartnerApi';
import { useNotification } from 'app/notifications';
import { Student } from 'apis/StudentApi';
import { ListItemCount } from 'types';
import { useSuperState } from 'hooks';
import moment from 'moment';

import 'pages/reports/components/FilterWrapper/styles/filter-wrapper.scss';

interface FilterWrapperProps {
    handleClose: () => void;
    handleShow: () => void;
    mode?: string;
    onAccessCodeDataLoaded?: (accessCodes: Transaction[]) => void;
    onClassroomDataLoaded?: (classrooms: StudentGroupWithDetails[]) => void;
    onPartnerDataLoaded?: (partners: Partner[]) => void;
    onStudentDataLoaded?: (students: Student[]) => void;
    removeFilters?: string[];
    report: Report;
    reportFilters: ReportFilter[] | undefined;
    runActualReport: (override?: boolean) => Promise<void>
    scopeId: number;
    scopeObjectId: number;
    setLoadingReport: (loading: boolean) => void;
    setMode?: (mode: string) => void;
    setReport: (report: Report) => void;
    setReportFilters: (filters: ReportFilter[] | undefined) => void;
    showNoFilters?: boolean;
    showStartEndDateOnly?: boolean;

    /**
     * When `true` (default), filter changes sync back into the page URL so
     * report state is shareable / deep-linkable. Embedded contexts (e.g. the
     * Reports tab on the Students page) should pass `false` so that
     * per-filter values don't pollute the host page's query string.
     */
    urlSync?: boolean;
}

export function FilterWrapper({
    handleClose,
    handleShow,
    mode,
    onAccessCodeDataLoaded,
    onClassroomDataLoaded,
    onPartnerDataLoaded,
    onStudentDataLoaded,
    removeFilters,
    report,
    reportFilters,
    runActualReport,
    scopeId,
    scopeObjectId,
    setLoadingReport,
    setMode,
    setReport,
    setReportFilters,
    showNoFilters = false,
    showStartEndDateOnly = false,
    urlSync = true
}: FilterWrapperProps) {

    const [state, setState] = useSuperState({
        classroomList: [] as StudentGroupWithDetails[],
        datePresetResetVersion: 0,
        filteredClassrooms: [] as StudentGroupWithDetails[],
        groupKendoCount: { count: 0, firstId: 0 } as ListItemCount,
        loadingClassrooms: false,
        partnerKendoCount: { count: 0, firstId: 0 } as ListItemCount,
        runOnce: true,
        selectedClassrooms: [] as StudentGroupWithDetails[],
        selectedStudent: null as Student | null,
        showArchivedClassrooms: false,
        studentKendoCount: { count: 0, firstId: 0 } as ListItemCount,
        studentList: [] as Student[],
        testsFilterOptions: [] as Option[],
        transactionKendoCount: { count: 0, firstId: 0 } as ListItemCount
    });

    const {
        classroomList, datePresetResetVersion, filteredClassrooms,
        groupKendoCount, loadingClassrooms, partnerKendoCount,
        runOnce, selectedClassrooms, selectedStudent,
        showArchivedClassrooms,
        studentKendoCount, studentList, testsFilterOptions, transactionKendoCount
    } = state;

    // --- Context ---
    const { currentUserId } = useContext(StudentsContext);
    const { classroomCache } = useContext(ReportsDefaultContext);
    const intl = useIntl();
    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    // --- Refs ---
    const studentsInitializedRef = useRef(false);
    const partnerDialogRef = useRef<PartnerGMetrixTableDialogHandle>(null);
    const transactionDialogRef = useRef<TransactionGMetrixTableDialogHandle>(null);

    // ----------------------------------------------
    //  Helper functions
    // ----------------------------------------------

    const findStudentFilterIndex = (): number => {

        if (!reportFilters) return -1;
        return reportFilters.findIndex(f => f.FieldType === 'StudentSearch');

    };

    const getDatePickerDefaultDays = (defaultValue?: string): number => {

        const trimmed = (defaultValue ?? '').trim();
        if (!trimmed) return 7000;
        try {

            const parsed = JSON.parse(trimmed);
            const days = parsed?.value ?? parsed?.Value ?? parsed?.days ?? parsed?.Days;
            if (typeof days === 'number' && days >= 0) return days;

        } catch {

            // fall through to string parsing
        }
        const numeric = Number(trimmed);
        if (!Number.isNaN(numeric) && numeric >= 0) return numeric;
        const match = trimmed.match(/\d+/);
        return match ? Number(match[0]) : 7000;

    };

    const buildDateRangeFromDays = (days: number): string => {

        const end = moment();
        const start = moment().subtract(days, 'days');
        return `${start.format('MM/DD/YYYY')}|${end.format('MM/DD/YYYY')}`;

    };

    // Reports that default the StartEndDate window to 7 days instead of "Since Start".
    const startEndDateDefaultDays = getStartEndDateDefaultDays(report.Id);

    const updateUrlParam = (key: string, value: string) => {

        if (!urlSync) return;
        const url = new URL(window.location.href);
        url.searchParams.set(key, value);
        window.history.pushState(null, '', url);

    };

    const filterClassName = (filterName: string) => `filter-wrapper__field ${removeFilters?.includes(filterName) ? 'd-none' : ''}`;

    const isFilterFilled = (f: ReportFilter) => {

        if (removeFilters?.includes(f.Name)) return true;
        const value = f.Value ?? '';
        switch (f.FieldType) {

            case 'DropDownList': return value !== '' && value !== '0';
            case 'CheckBox': return value !== '';
            default: return value !== '';

        }

    };

    const updateFilterValue = (index: number, value: string) => {

        if (!reportFilters) return;
        const updated = [...reportFilters];
        updated[index].Value = value;
        setReportFilters(updated);
        setReport({ ...report, ReportFilters: updated });

    };

    const writeFilter = (index: number, value: string, urlKey?: string) => {

        if (!reportFilters) return;
        const updated = [...reportFilters];
        updated[index].Value = value;
        setReportFilters(updated);
        setReport({ ...report, ReportFilters: updated });
        if (urlKey) updateUrlParam(urlKey, value);

    };

    const requiredFiltersFilled = useMemo(
        () => reportFilters?.filter(f => f.FilterRequired && f.FieldType !== 'UICulture').every(isFilterFilled) ?? false,
        [reportFilters, removeFilters]
    );

    const hasVisibleFilters = useMemo(
        () => !!reportFilters && reportFilters.filter(f => f.FieldType !== 'UICulture').length > 0 && !showNoFilters,
        [reportFilters, showNoFilters]
    );

    // ----------------------------------------------
    //  Data fetching
    // ----------------------------------------------

    const fetchStudentsForClassrooms = async (classrooms: StudentGroupWithDetails[]) => {

        if (classrooms.length === 0) {

            setState({ selectedStudent: null, studentList: [] });
            return;

        }
        try {

            const ids = classrooms.map(c => c.Id);
            const students = await StudentGroupApi.GetAllStudentsForGroups(ids);
            const seen = new Set<number>();
            const uniqueStudents = students
                .filter(s => {

                    if (seen.has(s.Id)) return false;
                    seen.add(s.Id);
                    return true;

                })
                .sort((a, b) => (a.Fullname || '').localeCompare(b.Fullname || ''));
            setState({ studentList: uniqueStudents });

        } catch {

            setState({ studentList: [] });
            createToast(formatMessage({ id: 'failed_to_load_students_for_selected_cla.4ef401ab', defaultMessage: 'Failed to load students for selected classrooms.' }), 'danger');

        }

    };

    const grabClassroomData = async () => {

        const cacheKey = `${report.Id}_${scopeId}_${scopeObjectId}`;
        const cached = classroomCache.get(cacheKey);
        if (cached) {

            setState({ classroomList: cached });
            return;

        }
        setState({ loadingClassrooms: true });
        try {

            const isStudentScope = scopeId === 8 || scopeId === 10;
            const classroomData = isStudentScope
                ? await StudentGroupApi.GetAll('Student', scopeObjectId)
                : await StudentGroupApi.GetAll('Partner', -1, scopeObjectId);
            if (classroomData) {

                classroomCache.set(cacheKey, classroomData);
                setState({ classroomList: classroomData });

            }

        } finally {

            setState({ loadingClassrooms: false });

        }

    };

    const loadPartners = async () => {

        const partnerFilter = reportFilters?.find(f => f.FieldType === 'PartnerSearch');
        if (!partnerFilter) return;

        try {

            const partnerType = parseInt(partnerFilter.Type) || 0;
            const partners = await PartnerApi.GetAll(partnerType);
            if (partners) {

                partners.forEach(p => (p.Id = p.ID));
                onPartnerDataLoaded?.(partners);

            }

        } catch {

            createToast(formatMessage({ id: 'failed_to_load_partners.eaa59017', defaultMessage: 'Failed to load partners.' }), 'danger');

        }

    };

    const loadAccessCodes = async () => {

        const transactionFilter = reportFilters?.find(f => f.FieldType === 'TransactionSearch');
        if (!transactionFilter) return;

        try {

            const transactions = await TransactionApi.GetAll(1, '', 10, scopeObjectId);
            if (transactions) {

                onAccessCodeDataLoaded?.(transactions);

            }

        } catch {

            createToast(formatMessage({ id: 'failed_to_load_access_codes.242fe9e5', defaultMessage: 'Failed to load access codes.' }), 'danger');

        }

    };

    // ----------------------------------------------
    //  Reset handler
    // ----------------------------------------------

    const handleResetFilters = () => {

        if (!reportFilters) return;

        const reset = reportFilters.map(filter => ({
            ...filter,
            Value: filter.FieldType === 'DatePicker'
                ? buildDateRangeFromDays(filter.Name === 'StartEndDate' ? startEndDateDefaultDays : getDatePickerDefaultDays(filter.DefaultValue))
                : filter.DefaultValue ?? ''
        }));

        setReportFilters(reset);
        setReport({ ...report, ReportFilters: reset });
        setState({
            datePresetResetVersion: datePresetResetVersion + 1,
            selectedClassrooms: [],
            selectedStudent: null,
            showArchivedClassrooms: false
        });

        if (urlSync) {

            const url = new URL(window.location.href);
            reset.forEach(filter => {

                if (filter.FieldType === 'DatePicker') {

                    url.searchParams.set(
                        filter.Name,
                        filter.Value ?? buildDateRangeFromDays(
                            filter.Name === 'StartEndDate' ? startEndDateDefaultDays : getDatePickerDefaultDays(filter.DefaultValue)
                        )
                    );

                } else {

                    url.searchParams.delete(filter.Name);

                }

            });
            window.history.pushState(null, '', url);

        }

    };

    // ----------------------------------------------
    //  Effects
    // ----------------------------------------------

    // Auto-select single-item kendo counts
    useEffect(() => {

        const autoSet: { kendoCountRef: ListItemCount; type: string }[] = [];
        reportFilters?.forEach(filter => {

            if (filter.FieldType === 'TransactionSearch' && transactionKendoCount.count === 1)
                autoSet.push({ kendoCountRef: transactionKendoCount, type: filter.FieldType });
            else if (filter.FieldType === 'StudentGroupsSearch' && groupKendoCount.count === 1)
                autoSet.push({ kendoCountRef: groupKendoCount, type: filter.FieldType });
            else if (filter.FieldType === 'StudentSearch' && studentKendoCount.count === 1)
                autoSet.push({ kendoCountRef: studentKendoCount, type: filter.FieldType });
            else if (filter.FieldType === 'PartnerSearch' && partnerKendoCount.count === 1)
                autoSet.push({ kendoCountRef: partnerKendoCount, type: filter.FieldType });

        });
        if (autoSet.length > 0 && reportFilters && runOnce) {

            const updated = [...reportFilters];
            autoSet.forEach(({ kendoCountRef, type }) => {

                const idx = updated.findIndex(f => f.FieldType === type);
                if (idx !== -1) updated[idx].Value = kendoCountRef.firstId.toString();

            });
            setReportFilters(updated);
            setState({ runOnce: false });

        }

    }, [groupKendoCount, transactionKendoCount, partnerKendoCount, studentKendoCount]);

    // Load data on mount (only for filters the report actually uses)
    useEffect(() => {

        if (reportFilters?.some(f => f.FieldType === 'PartnerSearch')) loadPartners();
        if (reportFilters?.some(f => f.FieldType === 'StudentGroupsSearch')) grabClassroomData();
        if (reportFilters?.some(f => f.FieldType === 'TransactionSearch')) loadAccessCodes();

    }, []);

    // Deduplicate & filter archived classrooms
    useEffect(() => {

        const unique = classroomList.filter((v, i, a) => a.findIndex(v2 => v2.Id === v.Id) === i);
        setState({ filteredClassrooms: showArchivedClassrooms ? unique : unique.filter(g => g.Archived === false) });

    }, [classroomList, showArchivedClassrooms]);

    // Propagate classroom data to parent for read-only filter display
    useEffect(() => {

        if (classroomList.length > 0) onClassroomDataLoaded?.(classroomList);

    }, [classroomList]);

    // Propagate student data to parent for read-only filter display
    useEffect(() => {

        if (studentList.length > 0) onStudentDataLoaded?.(studentList);

    }, [studentList]);

    // Sync selected classrooms from filter value
    useEffect(() => {

        const studentGroupsFilter = reportFilters?.find(f => f.FieldType === 'StudentGroupsSearch');
        if (studentGroupsFilter?.Value) {

            const selectedIds = studentGroupsFilter.Value.split(',').map(Number);
            const newClassrooms = filteredClassrooms.filter(c => selectedIds.includes(c.Id));
            setState({ selectedClassrooms: newClassrooms });
            if (!studentsInitializedRef.current && newClassrooms.length > 0) {

                studentsInitializedRef.current = true;
                fetchStudentsForClassrooms(newClassrooms);

            }

        }

    }, [filteredClassrooms, reportFilters]);

    useEffect(() => {

        const studentFilterIdx = findStudentFilterIndex();
        const studentFilter = studentFilterIdx !== -1 ? reportFilters?.[studentFilterIdx] : undefined;

        if (studentList.length === 0) return;

        if (selectedStudent && !studentList.find(s => s.Id === selectedStudent.Id)) {

            setState({ selectedStudent: null });
            if (studentFilterIdx !== -1 && reportFilters) {

                const updated = [...reportFilters];
                updated[studentFilterIdx].Value = '';
                setReportFilters(updated);

            }
            return;

        }

        if (!selectedStudent && studentFilter?.Value) {

            const student = studentList.find(s => s.Id === parseInt(studentFilter.Value!));
            if (student) setState({ selectedStudent: student });

        }

    }, [studentList, reportFilters]);

    // Clear ClassroomTests selection when it no longer matches the selected product
    useEffect(() => {

        if (!reportFilters || testsFilterOptions.length === 0) return;

        const productFilter = reportFilters.find(f => f.Name === 'ClassroomProducts');
        const testsFilter = reportFilters.find(f => f.Name === 'ClassroomTests');
        if (!testsFilter?.Value || testsFilter.Value === '0') return;
        if (!productFilter?.Value || productFilter.Value === '0') return;

        const matchedProduct = testsFilterOptions.find(o => o.Value === productFilter.Value);
        if (!matchedProduct?.ProductId) return;

        const selectedProductId = matchedProduct.ProductId;
        const selectedValues = testsFilter.Value.split(',').map(value => value.trim()).filter(Boolean);
        const validValues = selectedValues.filter(value => {

            const test = testsFilterOptions.find(o => o.Value === value);
            return test && test.ProductId === selectedProductId;

        });

        if (validValues.length < selectedValues.length) {

            const testsIndex = reportFilters.findIndex(f => f.Name === 'ClassroomTests');
            if (testsIndex !== -1) {

                updateFilterValue(testsIndex, validValues.join(',') || '0');

            }

        }

    }, [reportFilters, testsFilterOptions]);

    // ----------------------------------------------
    //  Dropdown data mapper
    // ----------------------------------------------

    const getSelectedProductId = (): string | null => {

        if (!reportFilters) return null;
        const productFilter = reportFilters.find(f => f.Name === 'ClassroomProducts');
        if (!productFilter?.Value || productFilter.Value === '0') return null;
        const matchedOption = testsFilterOptions.find(o => o.Value === productFilter.Value);
        return matchedOption?.ProductId ?? null;

    };

    const getDropdownData = (filterName: string, filter: ReportFilter): Option[] | undefined => {

        if (filterName === 'ClassroomStudents') {

            return studentList.map(s => ({
                Text: s.Fullname ?? `${s.Firstname} ${s.Lastname}`,
                Value: String(s.Id)
            })) as Option[];

        }
        if (filterName === 'ClassroomProducts') {

            return testsFilterOptions.length > 0 ? testsFilterOptions : undefined;

        }
        if (filterName === 'ClassroomTests') {

            const selectedProductId = getSelectedProductId();
            if (testsFilterOptions.length > 0) {

                if (selectedProductId) {

                    return testsFilterOptions.filter(o => o.ProductId === selectedProductId);

                }
                return testsFilterOptions;

            }
            return undefined;

        }
        return undefined;

    };

    // ----------------------------------------------
    //  Classroom selection display text
    // ----------------------------------------------

    const classroomDisplayText = selectedClassrooms.length === 0
        ? formatMessage({ id: 'search_or_select.c112d3bd', defaultMessage: 'Search or select' })
        : selectedClassrooms.length === 1
            ? selectedClassrooms[0].Name
            : formatMessage({ id: 'count_selected.44440185', defaultMessage: '{count} Selected' }, { count: selectedClassrooms.length });

    const studentDisplayText = selectedClassrooms.length === 0
        ? formatMessage({ id: 'select_a_classroom_first.f23d16fd', defaultMessage: 'Select a classroom first' })
        : selectedStudent
            ? selectedStudent.Fullname
            : formatMessage({ id: 'search_or_select_a_student.c9aeb216', defaultMessage: 'Search or select a student' });

    // ----------------------------------------------
    //  Filter renderers
    // ----------------------------------------------

    const renderDropdownFilter = (filter: ReportFilter, i: number) => (
        <div className={filterClassName(filter.Name)} key={`${i}_DropDownList`}>
            <ReportDropdownList
                data={getDropdownData(filter.Name, filter)}
                filter={filter}
                filterIndex={i}
                key={i}
                onOptionsLoaded={filter.Name === 'ClassroomTests' ? (opts: Option[]) => setState({ testsFilterOptions: opts }) : undefined}
                report={report}
                reportFilters={reportFilters}
                scopeId={scopeId}
                scopeObjectId={scopeObjectId}
                setReport={setReport}
                setReportFilters={setReportFilters}
                urlSync={urlSync}
            />
        </div>
    );

    const renderDatePickerFilter = (filter: ReportFilter, i: number) => (
        <div className={filterClassName(filter.Name)} key={`${i}_DatePicker`}>
            <StartDateFilter
                dateValidation={{ allowFutureDates: false }}
                report={report}
                reportFilterIndex={i}
                reportFilters={reportFilters}
                resetVersion={datePresetResetVersion}
                setReport={setReport}
                urlSync={urlSync}
            />
        </div>
    );

    const renderTransactionSearchFilter = (filter: ReportFilter, i: number) => (
        <div className={filterClassName(filter.Name)} key={`${i}_TransactionSearch`}>
            <GMetrixButton
                onClick={async () => {

                    const result = await transactionDialogRef.current?.show({
                        filter,
                        multiSelect: filter.MultiSelect,
                        ...transactionFilterPickerWording(intl, filter.MultiSelect)
                    });
                    if (!result) return;

                    writeFilter(i, result.ids.toString(), 'TransactionsCSV');
                    if (result.transactions) onAccessCodeDataLoaded?.(result.transactions);

                }}
                label={formatMessage({ id: 'access_code.b5fc1b57', defaultMessage: 'Access Code' })}
            />
        </div>
    );

    const renderClassroomSearchFilter = (filter: ReportFilter, i: number) => (
        <div className={filterClassName(filter.Name)} key={`${i}_StudentGroupsSearch`}>
            <Row className="align-items-start">
                <Col md={7} xs={12}>
                    <GMetrixDropdown
                        filterable
                        multiSelect
                        customDisplayText={
                            loadingClassrooms
                                ? (
                                    <span className="d-flex align-items-center gap-2">
                                        <GMetrixLoader size="small" type="infinite-spinner" />
                                        {' '}
                                        <FormattedMessage defaultMessage="Loading..." id="loading.2350c7f9" />
                                    </span>
                                )
                                : classroomDisplayText
                        }
                        onBlur={async (classrooms: StudentGroupWithDetails | StudentGroupWithDetails[]) => {

                            const selectedClassroomsArr: StudentGroupWithDetails[] = Array.isArray(classrooms)
                                ? classrooms
                                : (classrooms ? [classrooms] : []);
                            const ids = selectedClassroomsArr.map(c => c.Id);
                            if (reportFilters) {

                                const allClassroomsSelected = filteredClassrooms.length > 0 && selectedClassroomsArr.length >= filteredClassrooms.length;
                                writeFilter(i, ids.toString());
                                updateUrlParam('StudentGroupsCSV', allClassroomsSelected ? 'All' : ids.toString());

                            }
                            await fetchStudentsForClassrooms(selectedClassroomsArr);

                        }}
                        onChange={classrooms => {

                            if (Array.isArray(classrooms)) setState({ selectedClassrooms: classrooms });

                        }}
                        dataItemKey="Id"
                        dataItems={filteredClassrooms}
                        disabled={loadingClassrooms}
                        label={formatMessage({ id: 'classroom.127e40cb', defaultMessage: 'Classroom' })}
                        textField="Name"
                        value={selectedClassrooms}
                    />
                </Col>
                <Col className="d-flex align-items-center pt-md-4 mt-md-2" md={5} xs={12}>
                    <div className="filter-wrapper__classroom-meta">
                        <OverlayTrigger
                            rootClose
                            overlay={(
                                <Tooltip id="classroom-filter-tooltip">
                                    {selectedClassrooms.length === 0
                                        ? 'No Classrooms Selected'
                                        : selectedClassrooms.map((c, idx) => (
                                            <div key={idx}>
                                                {c.Name}
                                            </div>
                                        ))}
                                </Tooltip>
                            )}
                            container={document.body}
                            placement="top"
                            trigger="click"
                        >
                            <span className="filter-wrapper__classroom-count">
                                {selectedClassrooms.length}
                                {' '}
                                <FormattedMessage defaultMessage="Classrooms Selected" id="classrooms_selected.5656a008" />
                            </span>
                        </OverlayTrigger>
                        <GMetrixCheckInput
                            onChange={e => {

                                setState({ showArchivedClassrooms: e.target.checked });
                                sessionStorage.setItem('archivedClassrooms', JSON.stringify(e.target.checked));

                            }}
                            checked={showArchivedClassrooms}
                            className="mt-2"
                            label={formatMessage({ id: 'include_archived.b6367e47', defaultMessage: 'Include Archived' })}
                            type={'checkbox'}
                        />
                    </div>
                </Col>
            </Row>
        </div>
    );

    const renderStudentSearchFilter = (filter: ReportFilter, i: number) => (
        <div className={filterClassName(filter.Name)} key={`${i}_StudentSearch`}>
            <GMetrixDropdown
                filterable
                onChange={student => {

                    const selected = student as Student;
                    setState({ selectedStudent: selected });
                    writeFilter(i, selected.Id.toString(), 'StudentsCSV');

                }}
                customDisplayText={studentDisplayText}
                dataItemKey="Id"
                dataItems={studentList}
                disabled={selectedClassrooms.length === 0}
                label={formatMessage({ id: 'student.4f46d2e3', defaultMessage: 'Student' })}
                textField="Fullname"
                value={selectedStudent}
            />
        </div>
    );

    const renderPartnerSearchFilter = (filter: ReportFilter, i: number) => (
        <div className={filterClassName(filter.Name)} key={`${i}_PartnerSearch`}>
            <GMetrixButton
                onClick={async () => {

                    const result = await partnerDialogRef.current?.show({
                        multiselect: filter.MultiSelect,
                        scopeId: parseInt(filter.Type),
                        ...partnerFilterPickerWording(intl, parseInt(filter.Type), filter.MultiSelect)
                    });
                    if (!result) return;

                    writeFilter(i, result.ids.toString(), 'PartnersCSV');

                }}
                label={formatMessage({ id: 'partner.cb022868', defaultMessage: 'Partner' })}
            />
        </div>
    );

    const renderCheckboxFilter = (filter: ReportFilter, i: number) => (
        <div className={filterClassName(filter.Name)} key={`${filter.Name}_CheckBox`}>
            <GMetrixCheckInput
                onChange={e => {

                    if (e.target.checked !== undefined) writeFilter(i, e.target.checked.toString(), filter.Name);

                }}
                checked={filter.Value === '1' || filter.Value === 'true'}
                className="py-3"
                label={filter.DisplayName}
                type={'checkbox'}
            />
        </div>
    );

    const renderDefaultFilter = (filter: ReportFilter, i: number) => (
        <div className={filterClassName(filter.Name)} key={`${filter.Name}_Text`}>
            <GMetrixInput
                onChange={e => {

                    if (e.target.value !== undefined) writeFilter(i, e.target.value.toString(), filter.Name);

                }}
                label={filter.DisplayName}
                value={filter.Value}
            />
        </div>
    );

    const renderFilter = (filter: ReportFilter, i: number) => {

        switch (filter.FieldType) {

            case 'DropDownList': return renderDropdownFilter(filter, i);
            case 'DatePicker': return renderDatePickerFilter(filter, i);
            case 'TransactionSearch': return renderTransactionSearchFilter(filter, i);
            case 'StudentGroupsSearch': return renderClassroomSearchFilter(filter, i);
            case 'StudentSearch': return renderStudentSearchFilter(filter, i);
            case 'PartnerSearch': return renderPartnerSearchFilter(filter, i);
            case 'UICulture': return <React.Fragment key={`${i}_UICulture`} />;
            case 'CheckBox': return renderCheckboxFilter(filter, i);
            default: return renderDefaultFilter(filter, i);

        }

    };

    // ----------------------------------------------
    //  Visible filter list
    // ----------------------------------------------

    const visibleFilters = reportFilters
        ?.filter(f => !showStartEndDateOnly || f.Id === ReportFilterType.StartEndDate) ?? [];

    // ----------------------------------------------
    //  Render
    // ----------------------------------------------

    return (
        <div className="filter-wrapper">
            {/* Dialogs (rendered outside visible flow) */}
            <TransactionGMetrixTableDialog
                neverDisableSuccessBtn
                currentUserId={currentUserId}
                objectId={scopeObjectId}
                option="Add"
                ref={transactionDialogRef}
                setTransactionKendoCount={count => setState({ transactionKendoCount: count as ListItemCount })}
                transactionAssociation={{ associationIds: [] as number[], associationName: 'Report' }}
            />
            <PartnerGMetrixTableDialog
                option="Change"
                ref={partnerDialogRef}
                setPartnerKendoCount={count => setState({ partnerKendoCount: count as ListItemCount })}
            />

            {/* Report header */}
            {report.Name && (
                <div className="filter-wrapper__header">
                    <h4 className="filter-wrapper__title">
                        {report.Name}
                    </h4>
                    <p className="filter-wrapper__subtitle">
                        <FormattedMessage defaultMessage="Set filters to run this report" id="set_filters_to_run_this_report.db241bf1" />
                    </p>
                </div>
            )}

            {/* Filter fields */}
            {hasVisibleFilters && (
                <div className="filter-wrapper__filters">
                    {visibleFilters.map((filter, i) => renderFilter(filter, i))}

                    {setMode && (
                        <div className="filter-wrapper__field" key="mode_filter">
                            <label className="fw-bold mb-2 d-block" style={{ fontSize: '0.875rem' }}>
                                <FormattedMessage defaultMessage="Mode" id="mode.f65b1f2b" />
                            </label>
                            <GMetrixButtonGroup
                                buttonObjectList={[
                                    { action: () => setMode('Testing'), title: formatMessage({ id: 'testing.a439953c', defaultMessage: 'Testing' }) },
                                    { action: () => setMode('Training'), title: formatMessage({ id: 'training.1fce7301', defaultMessage: 'Training' }) }
                                ]}
                                currentValue={mode}
                            />
                        </div>
                    )}

                    {reportFilters && reportFilters.length > 0 && (
                        <div className="filter-wrapper__actions">
                            <GMetrixButton onClick={handleResetFilters} variant="link">
                                <FormattedMessage defaultMessage="Reset Filters" id="reset_filters.1504ae20" />
                            </GMetrixButton>
                            <div className="filter-wrapper__actions-right">
                                <GMetrixButton className="me-2" onClick={handleClose} variant="outline-primary">
                                    <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                                </GMetrixButton>
                                <GMetrixButton
                                    onClick={() => {

                                        setLoadingReport(true);
                                        runActualReport(true);
                                        handleClose();

                                    }}
                                    disabled={!requiredFiltersFilled}
                                    variant="primary"
                                >
                                    <FormattedMessage defaultMessage="Run Report" id="run_report.78544a4a" />
                                </GMetrixButton>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

}
